import { useDisclosure } from "@heroui/react";
import { useCurrentAccount } from "@mysten/dapp-kit-react";
import { useEffect, useMemo } from "react";

import { AppModal } from "@/components/ui/AppModal";
import { useAuthenticationContext } from "@/context/authentication/useAuthenticationContext";
import { useClaimsContext } from "@/context/claims/useClaimsContext";
import { useSignPersonalMessage } from "@/hooks/dappkit";
import {
  formatBaseUnits,
  formatBaseUnitsForDisplay,
  parseExactBaseUnits,
  sanitizeDecimalInput,
  scaleBaseUnits,
  toBaseUnitsFloor,
  TRUTH_DECIMALS,
} from "@/lib/currency/tokenAmount";
import { Button } from "@/ui/nextui/Button/Button";
import { Input } from "@/ui/nextui/Input/Input";
import { useToast } from "@/ui/shadcn/use-toast";

/** Minimum claim, in base units. Mirrors the same rule on the API route. */
const MIN_CLAIM_UNITS = toBaseUnitsFloor(10)!;

/**
 * Quick-select presets, as a percentage of the claimable balance.
 *
 * Fractions below 100% are rounded to the nearest representable unit so 50% of
 * 162.99999999 reads as the 81.5 a user expects. "Max" is floored instead: it
 * has to stay at or below the server's balance, and every result is clamped to
 * that balance anyway.
 */
const QUICK_SELECT = [
  { label: "20%", percent: 20n, rounding: "round" as const },
  { label: "50%", percent: 50n, rounding: "round" as const },
  { label: "Max", percent: 100n, rounding: "floor" as const },
];

export const ClaimDailyRewards: React.FC = () => {
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();

  const account = useCurrentAccount();
  const {
    claimDailyRewards,
    claimDailyRewardsForm,
    calculateUnlockedRewardsResponse,
    isRewardsCardLoading,
  } = useClaimsContext();
  const { mutate: signPersonalMessage } = useSignPersonalMessage();
  const { generateNonce } = useAuthenticationContext();
  const { toast } = useToast();

  const amountText = claimDailyRewardsForm.watch("amount") || "";

  /**
   * Claimable balance in base units, floored.
   *
   * `unlockedRewards` is a float the API computes by summing products, so it
   * carries far more than 8 decimals. Flooring here makes the value payable and
   * guarantees the button we render as "Max" is never above what the server will
   * accept — rounding it (the old `.toFixed(2)`) turned 162.99999999999997 into
   * 163.00 and the claim was rejected as exceeding the available rewards.
   */
  const balanceUnits = useMemo(
    () => toBaseUnitsFloor(calculateUnlockedRewardsResponse?.unlockedRewards ?? 0) ?? 0n,
    [calculateUnlockedRewardsResponse?.unlockedRewards],
  );

  /**
   * The claim batcher's per-claim ceiling, or null for none.
   *
   * A claim above it is never paid — the batcher quarantines it and it shows up
   * in the history as "cancelled" weeks later. The API refuses one outright
   * now, and this caps the form so the user is never offered one.
   */
  const ceilingUnits = useMemo(() => {
    const raw = calculateUnlockedRewardsResponse?.maxClaimUnits;

    return raw ? BigInt(raw) : null;
  }, [calculateUnlockedRewardsResponse?.maxClaimUnits]);

  /** True when the ceiling, not the balance, is what limits this claim. */
  const isCappedByCeiling = ceilingUnits !== null && ceilingUnits < balanceUnits;

  /**
   * The most this claim may request: the balance, or the ceiling when lower.
   * Every limit in this form — "Max", the presets, the error — reads this.
   */
  const maxUnits = isCappedByCeiling ? ceilingUnits : balanceUnits;

  // null when the text is not a payable amount (empty, malformed, or more than
  // 8 decimals).
  const amountUnits = useMemo(() => parseExactBaseUnits(amountText), [amountText]);

  const isClaimingDisabled = !calculateUnlockedRewardsResponse?.isClaimingEnabled;

  /**
   * Single source of truth for the amount error.
   *
   * The input's message, the submit button's disabled state and the submit guard
   * all read this. Previously each re-derived its own rules with `parseFloat`,
   * which is how the modal could show one limit, disable on another, and toast a
   * third.
   */
  const amountError = useMemo(() => {
    if (!amountText.trim()) return "Enter an amount to claim.";

    if (amountUnits === null) {
      return `Enter a valid amount with at most ${TRUTH_DECIMALS} decimal places.`;
    }

    if (amountUnits < MIN_CLAIM_UNITS) {
      return `Minimum claim amount is ${formatBaseUnits(MIN_CLAIM_UNITS)} $TRUTH.`;
    }

    if (amountUnits > maxUnits) {
      return isCappedByCeiling
        ? `Amount cannot exceed ${formatBaseUnitsForDisplay(maxUnits)} $TRUTH, the maximum per claim.`
        : `Amount cannot exceed ${formatBaseUnits(maxUnits)} $TRUTH.`;
    }

    return null;
  }, [amountText, amountUnits, maxUnits, isCappedByCeiling]);

  const canSubmit =
    !isRewardsCardLoading && !isClaimingDisabled && !amountError && amountUnits !== null;

  function onAmountChange(value: string) {
    // Sanitising on the way in means an unpayable amount (letters, a second dot,
    // a 9th decimal) can never reach the form state.
    claimDailyRewardsForm.setValue("amount", sanitizeDecimalInput(value, TRUTH_DECIMALS), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }

  function onSubmit() {
    if (amountError || amountUnits === null) {
      toast({
        title: "Error",
        description: amountError ?? "Enter a valid amount to claim.",
        variant: "destructive",
      });

      return;
    }

    // The season score warning modal that could sit in between was removed with
    // the seasons (2026-09-27), so a valid amount goes straight to signing.
    onPressClaimDailyRewards(amountUnits);
  }

  async function onPressClaimDailyRewards(units: bigint) {
    if (!account?.address) return;

    // One canonical decimal string for the whole request: it is what the user
    // signs, and `Number()` of it is what the server re-parses out of both the
    // signed text and the body. Deriving the two from the same string is what
    // keeps the server's signed-amount comparison exact.
    const amountString = formatBaseUnits(units);
    const amount = Number(amountString);

    try {
      // Step 1: Generate nonce
      const nonceData = await generateNonce(account.address);

      if (!nonceData) {
        toast({
          title: "Error",
          description: "Failed to generate security nonce. Please try again.",
          variant: "destructive",
        });

        return;
      }

      // Step 2: Create message with nonce and timestamp.
      // The amount is part of the signed text so the server can bind the signature
      // to this specific claim, and so the wallet prompt shows what is authorized.
      const message = new TextEncoder().encode(
        `I confirm that I'm claiming $TRUTH tokens to be sent to my connected wallet. amount: ${amountString} nonce: ${nonceData.nonce}`,
      );

      // Step 3: Sign the message
      signPersonalMessage(
        {
          message,
        },
        {
          async onSuccess(data) {
            await claimDailyRewards({
              user_address: account.address,
              message: Array.from(message),
              signature: data.signature,
              nonce: nonceData.nonce,
              nonceToken: nonceData.nonceToken,
              amount,
            });

            onClose(); // Close modal after successful submission
          },
          onError(error) {
            toast({
              title: "Error",
              description: <p className="mb-0">While submitting your signature: {error.message}</p>,
              variant: "destructive",
            });
          },
        },
      );
    } catch {
      toast({
        title: "Error",
        description: "Failed to process claim request. Please try again.",
        variant: "destructive",
      });
    }
  }

  // Start every visit to the modal from an empty field rather than "0": "0" is
  // itself an invalid amount, so it made the form open in an error state.
  useEffect(() => {
    if (isOpen) {
      claimDailyRewardsForm.setValue("amount", "", { shouldValidate: false });
    }
  }, [isOpen]);

  // Check if we're still loading data
  const isLoading = !calculateUnlockedRewardsResponse;

  return (
    <>
      {isLoading ? (
        <div
          className="h-[44px] w-full animate-pulse rounded-none bg-gray-700"
          id="claim-daily-rewards-button-skeleton"
        />
      ) : (
        <Button
          fullWidth
          className="rounded-none"
          color={isClaimingDisabled ? "default" : "success"}
          id="claim-daily-rewards-open-button"
          size="lg"
          type="button"
          onPress={onOpen}
        >
          Claim Your Rewards
        </Button>
      )}

      <AppModal
        footer={
          <>
            <Button
              color="danger"
              id="claim-daily-rewards-cancel-button"
              variant="light"
              onPress={onClose}
            >
              Cancel
            </Button>
            <Button
              color="success"
              id="claim-daily-rewards-submit-button"
              isDisabled={!canSubmit}
              isLoading={isRewardsCardLoading}
              type="button"
              onPress={onSubmit}
            >
              {isClaimingDisabled ? (
                "Claiming Not Enabled"
              ) : (
                <>Claim {formatBaseUnitsForDisplay(amountUnits ?? 0n)} $TRUTH</>
              )}
            </Button>
          </>
        }
        id="claim-daily-rewards-modal"
        isOpen={isOpen}
        size="md"
        subtitle="Review and confirm your reward claim"
        title="Confirm Claim"
        onClose={onClose}
        onOpenChange={onOpenChange}
      >
        {/* The "Bonus Loss" never-claimed warning was removed on 2026-09-28: it
            read the score breakdown, which was only fetched inside a draw
            window, and the last one closed on 2026-06-06. */}
        {/* Claim Amount Display */}
        <div
          className="border-b border-gray-800 pb-4 text-center"
          id="claim-daily-rewards-available"
        >
          <p
            className="mb-1 text-sm font-bold uppercase tracking-widest text-green-500"
            id="claim-daily-rewards-available-label"
          >
            Available to Claim
          </p>
          <h2
            className="mb-2 text-4xl font-bold text-white"
            id="claim-daily-rewards-available-value"
          >
            {/* Full 8-decimal precision, truncated rather than rounded: the
                headline must never read higher than the form will accept, and
                capping it at 2 decimals made "Max" fill in an amount that did
                not match the balance shown right above it. */}
            {/* The BALANCE, not the per-claim maximum: capping the headline
                would make the rest of the user's rewards look gone. */}
            {formatBaseUnitsForDisplay(balanceUnits)}
          </h2>
          <p className="text-sm text-gray-400" id="claim-daily-rewards-available-ticker">
            $TRUTH
          </p>
          {isCappedByCeiling && (
            <p className="mb-0 mt-2 text-xs text-yellow-400" id="claim-daily-rewards-ceiling-note">
              Up to {formatBaseUnitsForDisplay(maxUnits)} $TRUTH per claim. The rest stays available
              for a later claim.
            </p>
          )}
        </div>

        {/* Amount Selection */}
        <div className="border-b border-gray-800 pb-4 pt-4" id="claim-daily-rewards-amount-section">
          <Input
            classNames={{
              inputWrapper: "bg-gray-800 border border-gray-700",
              input: "text-right font-bold text-lg",
            }}
            endContent={
              <div
                className="pointer-events-none flex items-center"
                id="claim-daily-rewards-amount-suffix"
              >
                <span
                  className="text-small text-default-400"
                  id="claim-daily-rewards-amount-ticker"
                >
                  $TRUTH
                </span>
              </div>
            }
            errorMessage={amountError}
            // Deliberately a text field. With `type="number"` the browser ran its
            // own constraint validation against the default step of 1, so any
            // fractional amount was reported under the input as invalid ("the two
            // nearest valid values are 81 and 82") even though 8 decimals are
            // perfectly claimable. `inputMode="decimal"` keeps the numeric keypad
            // on mobile, and a text field also can't be changed by scrolling.
            id="claim-daily-rewards-amount-input"
            inputMode="decimal"
            isInvalid={!!amountError && !!amountText.trim()}
            label="Claim Amount"
            placeholder="0.00000000"
            type="text"
            validationBehavior="aria"
            value={amountText}
            variant="bordered"
            onValueChange={onAmountChange}
          />

          <p
            className="mb-2 mt-4 text-center text-xs text-gray-400"
            id="claim-daily-rewards-quick-select-label"
          >
            Quick Select
          </p>
          <div className="grid grid-cols-3 gap-2" id="claim-daily-rewards-quick-select-options">
            {QUICK_SELECT.map(({ label, percent, rounding }) => {
              // All of it in base units, so no float dust and no chance of
              // landing above the balance. Percentages are of `maxUnits` — the
              // most this claim may request — not of the balance: with a 2M
              // ceiling on a 12M balance, 20% and 50% of the balance both clamp
              // to the ceiling and all three presets collapse onto "Max".
              const presetUnits =
                percent === 100n ? maxUnits : scaleBaseUnits(maxUnits, percent, 100n, rounding);
              const clampedUnits = presetUnits > maxUnits ? maxUnits : presetUnits;
              // Exact comparison: the preset and the field hold the same integer.
              const isSelected =
                amountUnits !== null && amountUnits > 0n && amountUnits === clampedUnits;

              return (
                <Button
                  key={label}
                  className={isSelected ? "border-primary bg-primary/20 text-primary" : ""}
                  color={isSelected ? "primary" : "default"}
                  id={`claim-daily-rewards-quick-select-${percent}`}
                  isDisabled={clampedUnits <= 0n}
                  size="sm"
                  variant="bordered"
                  onPress={() =>
                    claimDailyRewardsForm.setValue("amount", formatBaseUnits(clampedUnits), {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Additional Info */}
        <div className="pt-4 text-center" id="claim-daily-rewards-footnote">
          {isClaimingDisabled ? (
            <p className="mb-0 text-xs text-yellow-400" id="claim-daily-rewards-disabled-note">
              Claiming is not yet enabled. Please check back later.
            </p>
          ) : (
            <p className="mb-0 text-xs text-gray-400" id="claim-daily-rewards-minimum-note">
              Minimum claim amount: {formatBaseUnits(MIN_CLAIM_UNITS)} $TRUTH · up to{" "}
              {TRUTH_DECIMALS} decimal places
            </p>
          )}
        </div>
      </AppModal>
    </>
  );
};
